import { createFileRoute, Link } from "@tanstack/react-router";
import { ChevronLeft, Wrench, Fuel, AlertCircle, CalendarClock, MessageCircle } from "lucide-react";
import { AppShell } from "@/components/app-shell";
import { DCard, HeroCard, Pill, SectionHeader, formatCentsInline } from "@/components/dryvn";
import { CollapsibleList } from "@/components/collapsible-list";
// Screen migration (2026-07-05): vehicles via hook.
import { useVehicles } from "@/lib/queries";

export const Route = createFileRoute("/more/vehicles")({
  head: () => ({
    meta: [
      { title: "Vehicles — Dryvn Life" },
      {
        name: "description",
        content: "Odometer, fuel efficiency, and full service history for the family vehicles.",
      },
    ],
  }),
  component: VehiclesPage,
});

const urgencyTone = (u: "ok" | "soon" | "overdue"): "life" | "amber" | "warm" =>
  u === "ok" ? "life" : u === "soon" ? "amber" : "warm";

function VehiclesPage() {
  const { data: vehicles } = useVehicles();
  return (
    <AppShell>
      <Link
        to="/more"
        className="mb-4 inline-flex items-center gap-1 text-sm"
        style={{ color: "var(--dryvn-primary)" }}
      >
        <ChevronLeft className="h-4 w-4" /> More
      </Link>

      <HeroCard
        eyebrow="Vehicles"
        title={
          <>
            Kept{" "}
            <em className="italic" style={{ color: "var(--dryvn-primary-deep)" }}>
              running
            </em>
            .
          </>
        }
      >
        <p className="text-sm" style={{ color: "var(--dryvn-primary-deep)" }}>
          Log a fill-up or a service by text — Dryvn tracks the rest.
        </p>
      </HeroCard>

      <div className="mt-6">
        <CollapsibleList items={vehicles} resource="vehicles">
          {(shownVehicles) => (
            <div className="grid gap-5 lg:grid-cols-2">
              {shownVehicles.map((v) => {
                const totalCost = v.history.reduce((sum, h) => sum + (h.costCents ?? 0), 0);
                return (
                  <DCard key={v.id} className="p-0 overflow-hidden">
                    <div className="relative p-5" style={{ backgroundImage: "var(--grad-lime)" }}>
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <p className="font-mono text-[10px] uppercase tracking-widest text-white/80">
                            {v.color} · {v.plate}
                          </p>
                          <h2 className="mt-1 font-display text-2xl leading-tight text-white">
                            {v.name}
                          </h2>
                        </div>
                        <span
                          className="rounded-full px-2.5 py-0.5 text-[11px] font-medium"
                          style={{
                            backgroundColor: "rgba(255,255,255,0.85)",
                            color: "var(--dryvn-primary-deep)",
                          }}
                        >
                          {v.odometer.toLocaleString()} km
                        </span>
                      </div>
                    </div>

                    <div
                      className="grid grid-cols-2 divide-x"
                      style={{ borderColor: "var(--dryvn-line)" }}
                    >
                      <div className="p-4">
                        <p
                          className="font-mono text-[10px] uppercase tracking-widest"
                          style={{ color: "var(--dryvn-grey)" }}
                        >
                          Next service
                        </p>
                        <p
                          className="mt-1 text-sm font-medium"
                          style={{ color: "var(--dryvn-ink)" }}
                        >
                          {v.nextService.name}
                        </p>
                        <div className="mt-2">
                          <Pill tone={urgencyTone(v.nextService.urgency)}>
                            {v.nextService.dueIn}
                          </Pill>
                        </div>
                      </div>
                      <div className="p-4">
                        <p
                          className="font-mono text-[10px] uppercase tracking-widest"
                          style={{ color: "var(--dryvn-grey)" }}
                        >
                          Fuel avg
                        </p>
                        <p
                          className="mt-1 flex items-baseline gap-1 text-sm font-medium"
                          style={{ color: "var(--dryvn-ink)" }}
                        >
                          <Fuel className="h-3.5 w-3.5" style={{ color: "var(--dryvn-primary)" }} />
                          <span className="font-display text-lg">{v.fuelMpg}</span>
                          <span className="text-[11px]" style={{ color: "var(--dryvn-grey)" }}>
                            {v.fuelUnit}
                          </span>
                        </p>
                      </div>
                    </div>

                    {v.notes && (
                      <div
                        className="flex items-start gap-2 px-5 py-3 text-xs"
                        style={{
                          borderTop: "1px solid var(--dryvn-line)",
                          backgroundColor:
                            "color-mix(in oklab, var(--dryvn-warm) 10%, transparent)",
                          color: "var(--dryvn-warm-deep)",
                        }}
                      >
                        <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                        <span>{v.notes}</span>
                      </div>
                    )}

                    <div className="p-5" style={{ borderTop: "1px solid var(--dryvn-line)" }}>
                      <div className="mb-3 flex items-center gap-2">
                        <CalendarClock
                          className="h-3.5 w-3.5"
                          style={{ color: "var(--dryvn-primary)" }}
                        />
                        <p
                          className="font-mono text-[10px] uppercase tracking-widest"
                          style={{ color: "var(--dryvn-grey)" }}
                        >
                          Upcoming
                        </p>
                      </div>
                      <ul className="space-y-1.5">
                        {v.upcoming.map((u) => (
                          <li key={u.name} className="flex items-baseline justify-between text-xs">
                            <span style={{ color: "var(--dryvn-ink)" }}>{u.name}</span>
                            <span className="font-mono" style={{ color: "var(--dryvn-grey)" }}>
                              {u.when}
                            </span>
                          </li>
                        ))}
                      </ul>
                    </div>

                    <div className="p-5" style={{ borderTop: "1px solid var(--dryvn-line)" }}>
                      <div className="mb-3 flex items-center justify-between">
                        <p
                          className="font-mono text-[10px] uppercase tracking-widest"
                          style={{ color: "var(--dryvn-grey)" }}
                        >
                          Service history
                        </p>
                        {totalCost > 0 && (
                          <span
                            className="font-mono text-[11px]"
                            style={{ color: "var(--dryvn-grey)" }}
                          >
                            Lifetime · {formatCentsInline(totalCost)}
                          </span>
                        )}
                      </div>
                      <CollapsibleList items={v.history} resource="services" threshold={5}>
                        {(shownHistory) => (
                          <ol
                            className="relative ml-1 border-l pl-5"
                            style={{ borderColor: "var(--dryvn-line)" }}
                          >
                            {shownHistory.map((h, i) => (
                              <li key={i} className="relative pb-4 last:pb-0">
                                <span
                                  className="absolute -left-[9px] top-1 grid h-4 w-4 place-items-center rounded-full ring-2"
                                  style={{
                                    backgroundColor: "var(--dryvn-primary)",
                                    boxShadow: "0 0 0 3px var(--dryvn-card)",
                                  }}
                                >
                                  <Wrench className="h-2 w-2 text-white" />
                                </span>
                                <div className="flex items-baseline justify-between gap-2">
                                  <p
                                    className="text-sm font-medium"
                                    style={{ color: "var(--dryvn-ink)" }}
                                  >
                                    {h.service}
                                  </p>
                                  <span
                                    className="font-mono text-xs"
                                    style={{ color: "var(--dryvn-grey)" }}
                                  >
                                    {h.km.toLocaleString()} km
                                  </span>
                                </div>
                                <div className="flex items-baseline justify-between">
                                  <p className="text-xs" style={{ color: "var(--dryvn-grey)" }}>
                                    {h.date}
                                  </p>
                                  {h.costCents !== undefined && (
                                    <span
                                      className="font-mono text-[11px]"
                                      style={{ color: "var(--dryvn-primary-deep)" }}
                                    >
                                      {formatCentsInline(h.costCents)}
                                    </span>
                                  )}
                                </div>
                              </li>
                            ))}
                          </ol>
                        )}
                      </CollapsibleList>
                    </div>
                  </DCard>
                );
              })}
            </div>
          )}
        </CollapsibleList>
      </div>

      <DCard className="mt-6">
        <SectionHeader title="Log by text" eyebrow="How" />
        <div className="grid gap-2 sm:grid-cols-2">
          <p
            className="rounded-lg px-3 py-1.5 font-mono text-xs"
            style={{
              backgroundColor: "var(--dryvn-page-tint)",
              color: "var(--dryvn-primary-deep)",
            }}
          >
            “oil change odyssey 84210 $89”
          </p>
          <p
            className="rounded-lg px-3 py-1.5 font-mono text-xs"
            style={{
              backgroundColor: "var(--dryvn-page-tint)",
              color: "var(--dryvn-primary-deep)",
            }}
          >
            “rav4 gas 52L $76.40”
          </p>
        </div>
        <p
          className="mt-3 flex items-center gap-1.5 text-[11px]"
          style={{ color: "var(--dryvn-grey)" }}
        >
          <MessageCircle className="h-3 w-3" /> Dryvn confirms and updates the log.
        </p>
      </DCard>
    </AppShell>
  );
}
